import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { MutableRefObject } from 'react'

import type { StatusVariant } from '@factorialco/f0-react'
import type { MarketplaceIntegrationWithIdsFragment } from 'generated/resources/graphql'
import {
  CreateSyncRunDocument,
  CreateSyncRunMutation,
  ExpensablesDataCollectionQuery,
  IntegrationsAgreggatedSyncableStateValuesEnum,
  IntegrationsCapabilityModeEnum,
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunOriginEnum,
  IntegrationsSyncableTypeEnum,
} from 'generated/resources/graphql'
import { print } from 'graphql'
import type { DocumentNode } from 'graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { GraphqlDataCollectionMappedVariables } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

import { toSyncParameters } from 'modules/integrations/lib/parameters'
import type { Parameters, SyncParameters } from 'modules/integrations/lib/parameters'
import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
import { SyncableStateAggregation } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { useAvailableIntegration } from 'modules/integrations/hooks/SyncContext/availableIntegration'
import { useSyncRunStatusChangedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncRunStatusChangedSubscription'
import { useSyncableStateUpdatedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncableStateUpdatedSubscription'
import {
  useReadLastSyncRun,
  useUpdateLastSyncRunQueryCache,
} from 'modules/integrations/hooks/SyncContext/useLastSyncRun'
import {
  useDataCollectionSourceState,
  type DataCollectionSourceState,
} from 'modules/integrations/hooks/SyncContext/useDataCollectionSourceState'
import { setSyncRunStatusIdSessionStorage } from 'modules/integrations/lib/syncRun'
import { useSyncRunFileDownload } from 'modules/integrations/hooks/SyncContext/useSyncRunFileDownload'

type ExpensesExpensableNode =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['edges'][number]['node']

export type SyncStatusColumnItem = ExpensesExpensableNode | EmployeePayrollRunNode

// Accept any GraphqlDataCollectionSource variant regardless of type parameters
// since we only use mappedVariables which is common to all variants
export type AnyGraphqlDataCollectionSource = {
  mappedVariables?: GraphqlDataCollectionMappedVariables
  mappedBaseVariables?: GraphqlDataCollectionMappedVariables
  document: DocumentNode
  [key: string]: unknown
}

export interface DataCollectionMappedVariable {
  key: string
  value: string
}

export type DataCollectionMappedVariables = DataCollectionMappedVariable[]

export interface Configuration {
  origin: IntegrationsSyncRunOriginEnum
  syncableType: IntegrationsSyncableTypeEnum
  isBookkeeperView?: boolean
  syncableStatesAggregation?: SyncableStateAggregation
  syncableStatusesFilter?: IntegrationsAgreggatedSyncableStateValuesEnum[]
  pollingEnabled?: boolean // When true, enables polling as a replacement for GraphQL subscriptions
  syncableEntityName: () => string // Returns human-readable name of the syncable entity (e.g. "payroll variables", "expenses", "compensations")
}

export type SyncStatusColumn = {
  label: string
  render: (item: SyncStatusColumnItem) => SyncStatusColumnValue | undefined
}

export type SyncStatusColumnValue = {
  type: 'status'
  value: {
    status: StatusVariant
    label: string
  }
}

export interface SyncContextType {
  isSyncing: boolean
  setContextParameters: (parameters: Parameters) => void
  setCompanyIdContext: (companyId: string) => void
  setCountry: (country: string | undefined) => void
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  setDataCollectionSource: (source: AnyGraphqlDataCollectionSource) => void
  isExportMode: boolean
}

export interface PrivateSyncContextType {
  runSynchronisation: () => Promise<void>
  isSyncRunCreating: boolean
  isSyncing: boolean
  contextParameters: Parameters
  syncParameters: () => SyncParameters
  setFilterParameters: (parameters: Parameters) => void
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  dataCollectionSourceState: DataCollectionSourceState
  dataCollectionSourceRef: MutableRefObject<AnyGraphqlDataCollectionSource | null>
  dataCollectionGraphqlJsonString: string | undefined
  configuration: Configuration
  capabilityMode?: IntegrationsCapabilityModeEnum | null
  isBookkeeperView: boolean
  companyId: string | undefined
  totalItems: number | undefined
  setTotalItems: (total: number) => void
}

const SyncContext = createContext<SyncContextType | null>(null)
const PrivateSyncContext = createContext<PrivateSyncContextType | null>(null)

export const IntegrationsSyncContextProvider = ({
  children,
  configuration,
}: {
  children: React.ReactNode
  configuration: Configuration
}) => {
  const {
    company: { id: sessionCompanyId },
  } = useCurrentSession()
  const isBookkeeperView = configuration.isBookkeeperView ?? false
  const [contextParameters, setContextParameters] = useState<Parameters>({})
  const [companyIdContext, setCompanyIdContext] = useState<string | undefined>(
    isBookkeeperView ? undefined : sessionCompanyId
  )
  const [filterParameters, setFilterParameters] = useState<Parameters>({})
  const [country, setCountry] = useState<string | undefined>(undefined)
  const [totalItems, setTotalItems] = useState<number | undefined>(undefined)
  const {
    state: dataCollectionSourceState,
    ref: dataCollectionSourceRef,
    setSource: setDataCollectionSource,
  } = useDataCollectionSourceState()

  const dataCollectionGraphqlJsonString = useMemo((): string | undefined => {
    const { document, mappedVariablesJson } = dataCollectionSourceState
    if (!document || !mappedVariablesJson) return undefined
    return JSON.stringify({
      query: print(document),
      variables: JSON.parse(mappedVariablesJson),
    })
  }, [dataCollectionSourceState.document, dataCollectionSourceState.mappedVariablesJson])

  const availableIntegration = useAvailableIntegration(
    configuration.origin,
    isBookkeeperView,
    companyIdContext,
    country
  )

  const marketplaceIntegration = availableIntegration?.marketplaceIntegration ?? null
  const capabilityMode = availableIntegration?.capabilityMode ?? null

  const syncEnabled = !!marketplaceIntegration

  const updateLastSyncRunQueryCache = useUpdateLastSyncRunQueryCache()

  const syncParameters = useCallback(() => {
    return toSyncParameters(contextParameters).concat(toSyncParameters(filterParameters))
  }, [contextParameters, filterParameters])

  const [sync, { loading: isSyncRunCreating }] = useMutation(CreateSyncRunDocument, {
    extractorFn: (data: CreateSyncRunMutation) => {
      return data.integrationsMutations.createIntegrationsSyncRun
    },
    update(_, { data }) {
      const syncRun = data?.integrationsMutations?.createIntegrationsSyncRun?.syncRun
      if (!syncRun || !marketplaceIntegration) return

      // Update the LastSyncRun query cache with the new created sync run
      // instead refetching to avoid DBs replication lag issues
      updateLastSyncRunQueryCache({
        marketplaceIntegration,
        contextParameters,
        syncRun,
        origin: configuration.origin,
      })
    },
  })

  const pollingEnabled = configuration.pollingEnabled ?? false

  const skipSubscriptions = !syncEnabled || pollingEnabled

  useSyncRunStatusChangedSubscription({
    marketplaceIntegration,
    contextParameters,
    origin: configuration.origin,
    skip: skipSubscriptions,
  })

  useSyncableStateUpdatedSubscription({
    marketplaceIntegration,
    skip: skipSubscriptions,
  })

  const runSynchronisation = useCallback(async () => {
    if (!syncEnabled || !marketplaceIntegration || !companyIdContext) return

    const result = await sync({
      variables: {
        companyId: companyIdContext,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: syncParameters(),
        graphqlVariablesJson: dataCollectionGraphqlJsonString,
      },
      context: {
        isBookkeeperView,
      },
    })

    const handledResult = handleResult(result, () => {})
    if (handledResult.success) {
      const syncRunId = handledResult?.data?.syncRun?.id
      if (syncRunId) {
        setSyncRunStatusIdSessionStorage(syncRunId.toString())
      }
    }
  }, [
    syncEnabled,
    marketplaceIntegration,
    companyIdContext,
    sync,
    configuration.origin,
    syncParameters,
    dataCollectionGraphqlJsonString,
    isBookkeeperView,
  ])

  const { data: lastSyncRunData, isLastSyncRunRunning } = useReadLastSyncRun({
    marketplaceIntegration,
    contextParameters,
    origin: configuration.origin,
    syncEnabled,
    isBookkeeperView,
    pollingEnabled,
  })

  const lastSyncRun = lastSyncRunData?.integrations?.syncRunsConnection.nodes[0] || null

  const { syncRunOutputsLoading } = useSyncRunFileDownload({
    isBookkeeperView,
    lastSyncRun,
  })

  const isSyncing = isSyncRunCreating || isLastSyncRunRunning || syncRunOutputsLoading

  const isExportMode = capabilityMode === 'file'

  const privateContextValue = useMemo<PrivateSyncContextType>(
    () => ({
      runSynchronisation,
      isSyncRunCreating,
      isSyncing,
      contextParameters,
      syncParameters,
      setFilterParameters,
      marketplaceIntegration,
      syncEnabled,
      lastSyncRun,
      dataCollectionSourceState,
      dataCollectionSourceRef,
      dataCollectionGraphqlJsonString,
      configuration,
      capabilityMode,
      isBookkeeperView,
      companyId: companyIdContext,
      totalItems,
      setTotalItems,
    }),
    [
      runSynchronisation,
      isSyncRunCreating,
      isSyncing,
      contextParameters,
      syncParameters,
      setFilterParameters,
      marketplaceIntegration,
      syncEnabled,
      lastSyncRun,
      dataCollectionSourceState,
      dataCollectionSourceRef,
      dataCollectionGraphqlJsonString,
      configuration,
      capabilityMode,
      isBookkeeperView,
      companyIdContext,
      totalItems,
      setTotalItems,
    ]
  )

  const publicContextValue = useMemo<SyncContextType>(
    () => ({
      isSyncing,
      setContextParameters,
      setCompanyIdContext,
      setCountry,
      marketplaceIntegration,
      syncEnabled,
      lastSyncRun,
      setDataCollectionSource,
      isExportMode,
    }),
    [
      isSyncing,
      setContextParameters,
      setCompanyIdContext,
      setCountry,
      marketplaceIntegration,
      syncEnabled,
      lastSyncRun,
      setDataCollectionSource,
      isExportMode,
    ]
  )

  return (
    <PrivateSyncContext.Provider value={privateContextValue}>
      <SyncContext.Provider value={publicContextValue}>{children}</SyncContext.Provider>
    </PrivateSyncContext.Provider>
  )
}

export function useIntegrationsSyncContext(): SyncContextType {
  const context = useContext(SyncContext)

  if (!context) {
    throw new Error('useIntegrationsSyncContext must be used within a SyncContextProvider')
  }

  return context
}

// As react hooks cannot be conditional, we need to use a flag to disable the context
export function usePrivateConditionalIntegrationsSyncContext(
  hidden = false
): PrivateSyncContextType | undefined {
  const context = useContext(PrivateSyncContext)
  if (hidden) return undefined

  if (!context) {
    throw new Error(
      'useConditionalIntegrationsSyncContext must be used within a SyncContextProvider'
    )
  }

  return context
}

export function usePrivateIntegrationsSyncContext() {
  const context = useContext(PrivateSyncContext)

  if (!context) {
    throw new Error(
      'usePrivateIntegrationsSyncContext must be used within a PrivateSyncContextProvider'
    )
  }

  return context
}
